import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Globe, ArrowLeft, ArrowRight, CheckCircle, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { CountryLabel } from "@/components/CountryLabel";
import { clearAssessmentDraft, readAssessmentDraft, writeAssessmentDraft } from "@/lib/assessmentDraft";
import { getGaPagePath, trackGaEvent } from "@/lib/ga4";
import { MOTIVATION_OPTIONS, normalizeMotivations } from "@/lib/motivationOptions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_OPTIONS = [
  { value: "cost_of_living", label: "Long-term affordability" },
  { value: "healthcare", label: "Healthcare quality" },
  { value: "safety", label: "Safety & stability" },
  { value: "residency_pathway_practicality", label: "Residency pathway practicality" },
  { value: "infrastructure_daily_convenience", label: "Infrastructure & daily convenience" },
  { value: "culture", label: "Daily pace & lifestyle fit" },
  { value: "climate", label: "Climate & weather" },
  { value: "language_transition_ease", label: "Language comfort & ease of transition" },
  { value: "career", label: "Career opportunities" },
  { value: "education", label: "Education quality" },
  { value: "community", label: "Expat community" },
  { value: "nature", label: "Nature & outdoors" },
  { value: "taxes", label: "Tax efficiency" },
];

const AGE_OPTIONS = [
  { value: "18-25", label: "18–25" },
  { value: "26-35", label: "26–35" },
  { value: "36-45", label: "36–45" },
  { value: "46-55", label: "46–55" },
  { value: "56-65", label: "56–65" },
  { value: "65+", label: "65+" },
];

const HOUSEHOLD_OPTIONS = [
  { value: "single", label: "Single", description: "This move is centered on one person." },
  { value: "couple", label: "Couple, no children in the move", description: "No children are part of the relocation decision." },
  { value: "family_young", label: "Family with young children", description: "Schooling, routine, and family stability matter." },
  { value: "family_older", label: "Family with teenage or older children", description: "Teen needs, timing, and longer-term planning matter." },
  { value: "retired", label: "Retired or nearing retirement", description: "Long-term livability and healthcare may carry more weight." },
];

const WORK_PROFILE_OPTIONS = [
  {
    value: "Employed full-time",
    label: "Employed",
    description: "You currently have a job, but the move may or may not depend on keeping that work setup.",
  },
  {
    value: "Self-employed / business owner",
    label: "Self-employed / business owner",
    description: "You work independently or run a business, which can shape residency and day-to-day setup differently.",
  },
  {
    value: "Retired",
    label: "Retired",
    description: "You are no longer working full time and the move is likely centered more on lifestyle, healthcare, and long-term practicality.",
  },
  {
    value: "Student",
    label: "Student",
    description: "You are studying now or the move may still be shaped by education-related timing and options.",
  },
  {
    value: "Between roles / flexible",
    label: "In transition / between roles",
    description: "You are between roles or in a transition period, so flexibility may matter more than a fixed work path.",
  },
  {
    value: "Financially independent / passive income",
    label: "Financially independent / passive income",
    description: "Your move is supported by savings, investments, or passive income rather than local work.",
  },
];

const WORK_LOCATION_FLEXIBILITY_OPTIONS = [
  { value: "must_be_in_specific_location", label: "Must be in a specific location", description: "Your work setup depends on staying in one place." },
  { value: "fully_remote", label: "Fully remote", description: "You can reliably keep working from another country." },
  { value: "some_flexibility", label: "Some flexibility", description: "You have some location freedom, but it still needs planning." },
];

const MOVE_SUPPORT_OPTIONS = [
  { value: "no_income_required", label: "The move would not depend on earning income after I relocate" },
  { value: "remote_income", label: "A remote job or remote income I could keep abroad" },
  { value: "self_employment", label: "Self-employment or business income" },
  { value: "retirement_income", label: "Retirement income" },
  { value: "passive_income", label: "Passive income or investments" },
  { value: "local_employment", label: "Local work or in-demand skills" },
  { value: "study_plans", label: "Study plans" },
  { value: "investment_capacity", label: "Investment capacity" },
  { value: "citizenship_by_descent", label: "Possible citizenship by descent" },
  { value: "not_sure", label: "Not sure yet" },
];

const BUDGET_OPTIONS = [
  { value: "under_2000", label: "Under $2,000/month", description: "A lower monthly range with tighter cost limits." },
  { value: "2000_2999", label: "$2,000–$2,999/month", description: "A lower-to-mid monthly budget range." },
  { value: "3000_3999", label: "$3,000–$3,999/month", description: "A mid-range monthly budget." },
  { value: "4000_5499", label: "$4,000–$5,499/month", description: "A higher mid-range monthly budget." },
  { value: "5500_7499", label: "$5,500–$7,499/month", description: "A broader budget range with more location flexibility." },
  { value: "7500_plus", label: "$7,500+/month", description: "An upper budget range with the widest location flexibility." },
];

const BUDGET_SELECTION_TO_SCORE_VALUE = {
  under_2000: "low",
  "2000_2999": "medium",
  "3000_3999": "medium",
  "4000_5499": "high",
  "5500_7499": "very_high",
  "7500_plus": "very_high",
};

const BUDGET_SCORE_TO_SELECTION_VALUE = {
  low: "under_2000",
  medium: "2000_2999",
  high: "4000_5499",
  very_high: "5500_7499",
};

const BUDGET_MODE_OPTIONS = [
  {
    value: true,
    label: "Keep my leading options close to budget",
    description: "Favor countries that stay nearer your stated monthly range.",
  },
  {
    value: false,
    label: "Allow some stretch for a stronger overall case",
    description: "Keep a few higher-cost countries in view if the broader case is still strong.",
  },
];

const LIFESTYLE_PREFERENCE_OPTIONS = [
  { value: "arts_culture", label: "Arts and culture" },
  { value: "fine_dining", label: "Food scene" },
  { value: "golf_access", label: "Golf access" },
  { value: "legalized_cannabis", label: "Cannabis legality" },
  { value: "nightlife_entertainment", label: "Nightlife and entertainment" },
  { value: "shopping", label: "Shopping" },
  { value: "sightseeing", label: "Sightseeing" },
  { value: "walkability", label: "Walkability and transit" },
  { value: "beach_environment", label: "Beach environment" },
  { value: "outdoor_activities", label: "Outdoor activities" },
];

const INFRASTRUCTURE_OPTIONS = [
  {
    value: "world_class",
    label: "World-class",
    description: "Highly reliable systems, strong healthcare access, efficient transport, and modern digital infrastructure.",
  },
  {
    value: "modern",
    label: "Modern",
    description: "Good everyday reliability with solid healthcare, internet, and transportation in most areas.",
  },
  {
    value: "adequate",
    label: "Adequate",
    description: "Comfortable enough for daily life, but with occasional gaps, slower systems, or regional inconsistency.",
  },
  {
    value: "basic",
    label: "Basic",
    description: "You are comfortable with simpler systems and more day-to-day tradeoffs if other parts of the move fit well.",
  },
];

const CLIMATE_OPTIONS = [
  { value: "warm", label: "Warm year-round" },
  { value: "tropical", label: "Tropical / humid" },
  { value: "temperate", label: "Temperate with seasons" },
  { value: "cooler", label: "Cooler / four-season" },
  { value: "any", label: "No strong climate preference" },
];

const LANGUAGE_OPTIONS = [
  { value: "english_only", label: "Strong preference for English-speaking environments" },
  { value: "comfortable_learning", label: "Comfortable learning if the overall case is strong" },
  { value: "willing_learn", label: "Actively willing to learn and use a new language" },
  { value: "any", label: "Language is not a major concern" },
];

const EMPTY_FORM_DATA = {
  natural_language_intro: "",
  first_name: "",
  current_country: "",
  citizenship: "",
  age_range: "",
  family_status: "",
  occupation: "",
  work_location_flexibility: "",
  remote_work: false,
  move_support_paths: [],
  work_abroad_intent: "",
  budget_range: "",
  strict_budget_mode: null,
  priorities: [],
  motivations: [],
  lifestyle_preferences: [],
  infrastructure_expectation: "",
  selected_countries: [],
  timeline: "",
  intended_length_of_stay: "",
  preferred_climate: "",
  language_flexibility: "",
  healthcare_importance: "",
  need_reliable_internet: null,
  expat_community_importance: "",
  alcohol_preference: "",
  cannabis_preference: "",
  additional_notes: "",
  request_followup: false,
};

const HEALTHCARE_OPTIONS = [
  { value: "very_important", label: "A top priority", description: "" },
  { value: "important", label: "Important", description: "" },
  { value: "moderate", label: "Moderately important", description: "" },
  { value: "not_priority", label: "Not a major priority", description: "" },
];

const COMMUNITY_OPTIONS = [
  { value: "very_important", label: "Very important" },
  { value: "important", label: "Important" },
  { value: "moderate", label: "Somewhat important" },
  { value: "not_important", label: "Not important" },
];

const INTERNET_OPTIONS = [
  { value: true, label: "Important", description: "Reliable internet would meaningfully shape day-to-day life and workability." },
  { value: false, label: "Not a major factor", description: "Internet quality matters, but it is not a primary filter." },
];

const TIMELINE_OPTIONS = [
  { value: "asap", label: "Within 1–3 months" },
  { value: "6_months", label: "Within 6 months" },
  { value: "1_year", label: "Within 1 year" },
  { value: "2_years", label: "Within 2 years" },
  { value: "2_years_or_more", label: "2 years or more" },
];

const TIMELINE_VALUE_ALIASES = {
  exploring: "2_years_or_more",
};

const normalizeTimelineValue = (value = "") => TIMELINE_VALUE_ALIASES[value] || value;

const STAY_LENGTH_OPTIONS = [
  { value: "trial_exploratory_move", label: "Trial stay / exploratory move" },
  { value: "6_12_month_stay", label: "6–12 month stay" },
  { value: "long_term_relocation", label: "Long-term relocation" },
  { value: "split_time_between_countries", label: "Split time between countries" },
];

const ASSESSMENT_STEPS = [
  {
    id: "motivations",
    section: "Why now",
    question: "What is pulling you toward living abroad right now?",
    helper: "Choose up to 3. We start here so the rest of the assessment stays grounded in what is actually driving the move.",
  },
  {
    id: "starting_point",
    section: "Starting point",
    question: "What country do you live in now, and what citizenship do you hold?",
    helper: "This helps us judge what may be practical from your starting point, especially around residency pathways and ease of transition.",
  },
  {
    id: "age_range",
    section: "About you",
    question: "Which age range are you in?",
    helper: "This helps us judge healthcare needs, timing, and longer-term practicality more realistically.",
  },
  {
    id: "household_setup",
    section: "About you",
    question: "What best describes your household setup?",
    helper: "This shapes housing needs, schooling considerations, stability, and day-to-day practicality.",
  },
  {
    id: "work_profile",
    section: "Practical realities",
    question: "What best describes your work or income situation right now?",
    helper: "This helps us understand how flexible the move may be and what kind of options are realistic.",
  },
  {
    id: "move_support_paths",
    section: "Practical realities",
    question: "What is most likely to support the move financially or practically?",
    helper: "Select all that apply. This helps us judge which residency pathways or longer stays may be realistic.",
  },
  {
    id: "budget",
    section: "Budget",
    question: "What monthly budget feels realistic for your life abroad, including housing and everyday living?",
    helper: "Think rent, groceries, transportation, and the version of daily life you would actually want to sustain.",
  },
  {
    id: "priorities",
    section: "What matters most",
    question: "Which factors should carry the most weight in your decision?",
    helper: "Select up to 5. These should be the factors that matter most in real life, not just in theory.",
  },
  {
    id: "lifestyle_preferences",
    section: "Everyday life",
    question: "What would make everyday life feel better?",
    helper: "Choose up to 3, then rank them. These help break close calls without outweighing the bigger decision factors.",
    optional: true,
  },
  {
    id: "infrastructure",
    section: "Practical realities",
    question: "How much day-to-day infrastructure and reliability do you want?",
    helper: "Think transport, healthcare access, internet, and everyday convenience.",
  },
  {
    id: "climate",
    section: "Everyday life",
    question: "What kind of climate feels best day to day?",
    helper: "This helps narrow everyday lifestyle fit without overpowering the bigger practical questions.",
  },
  {
    id: "language",
    section: "Everyday life",
    question: "How flexible are you about language?",
    helper: "This shows how much language comfort should shape daily life and ease of transition.",
  },
  {
    id: "healthcare",
    section: "Everyday life",
    question: "How important is healthcare quality?",
    helper: "Sets how strongly healthcare quality should shape the shortlist.",
  },
  {
    id: "settling_needs",
    section: "Settling in",
    question: "What would help you settle in with more confidence?",
    helper: "These lighter refiners help break close comparisons and shape how smooth the first stretch may feel.",
  },
  {
    id: "timing",
    section: "Move shape",
    question: "How soon are you hoping to move, and what kind of move is this?",
    helper: "Keeps the shortlist aligned with what feels realistic right now.",
  },
  {
    id: "countries_to_keep",
    section: "Countries to keep",
    question: "Are there any countries you want us to keep in view?",
    helper: "Optional. Use this if there are up to 3 countries you want included in your Assessment Results, even if they would not rise on their own.",
    optional: true,
  },
  {
    id: "review",
    section: "Review",
    question: "Review your answers",
    helper: "Jump back into any step before we build your Assessment Results or refresh your Blueprint.",
  },
];

const normalizeOccupation = (rawValue = "") => {
  const value = rawValue.trim();
  const lowered = value.toLowerCase();

  if (!value) return "";
  if (lowered.includes("retir") || lowered.includes("pension") || lowered.includes("empty nester")) return "Retired";
  if (lowered.includes("financially independent") || lowered.includes("passive income")) return "Financially independent / passive income";
  if (lowered.includes("student")) return "Student";
  if (lowered.includes("self") || lowered.includes("freelance") || lowered.includes("business") || lowered.includes("founder") || lowered.includes("consult")) {
    return "Self-employed / business owner";
  }
  if (lowered.includes("between") || lowered.includes("unemployed") || lowered.includes("sabbatical") || lowered.includes("seeking") || lowered.includes("transition")) {
    return "Between roles / flexible";
  }
  if (lowered.includes("employed") || lowered.includes("full time") || lowered.includes("full-time")) return "Employed full-time";
  if (WORK_PROFILE_OPTIONS.some((option) => option.value.toLowerCase() === lowered)) {
    return WORK_PROFILE_OPTIONS.find((option) => option.value.toLowerCase() === lowered)?.value || value;
  }

  return "Employed full-time";
};

const inferMoveSupportPathsFromSavedData = (data = {}) => {
  const savedPaths = Array.isArray(data.move_support_paths) ? data.move_support_paths : [];
  if (savedPaths.length > 0) {
    return savedPaths;
  }

  const inferredPaths = [];
  const occupation = (data.occupation || "").toLowerCase();
  const workAbroadIntent = data.work_abroad_intent || (data.remote_work ? "remote_self_employed" : "");

  if (workAbroadIntent === "remote_self_employed") {
    inferredPaths.push("remote_income");
  } else if (workAbroadIntent === "local_employment") {
    inferredPaths.push("local_employment");
  } else if (workAbroadIntent === "no") {
    inferredPaths.push("no_income_required");
  } else if (workAbroadIntent === "not_sure") {
    inferredPaths.push("not_sure");
  }

  if (occupation.includes("retired")) {
    inferredPaths.push("retirement_income");
  } else if (occupation.includes("financially independent") || occupation.includes("passive income")) {
    inferredPaths.push("passive_income");
  } else if (occupation.includes("student")) {
    inferredPaths.push("study_plans");
  } else if (occupation.includes("self-employed") || occupation.includes("business")) {
    inferredPaths.push("self_employment");
  }

  if (inferredPaths.includes("not_sure")) {
    return ["not_sure"];
  }

  return [...new Set(inferredPaths)];
};

const deriveWorkAbroadIntentFromMoveSupportPaths = (paths = []) => {
  const selectedPaths = new Set(paths || []);

  if (selectedPaths.has("not_sure")) return "not_sure";
  if (selectedPaths.has("local_employment")) return "local_employment";
  if (selectedPaths.has("remote_income") || selectedPaths.has("self_employment")) return "remote_self_employed";
  if (
    selectedPaths.has("no_income_required") ||
    selectedPaths.has("retirement_income") ||
    selectedPaths.has("passive_income") ||
    selectedPaths.has("study_plans") ||
    selectedPaths.has("investment_capacity") ||
    selectedPaths.has("citizenship_by_descent")
  ) {
    return "no";
  }

  return "";
};

const needsWorkLocationFlexibilityFollowUp = (occupation = "") => {
  const normalized = String(occupation).toLowerCase();
  return normalized.includes("employed") || normalized.includes("self-employed") || normalized.includes("business owner");
};

const mapBudgetSelectionToScoreValue = (selection) => BUDGET_SELECTION_TO_SCORE_VALUE[selection] || "";

const mapBudgetScoreValueToSelection = (scoreValue) => BUDGET_SCORE_TO_SELECTION_VALUE[scoreValue] || "";

const getChoiceCardClasses = (selected, subtle = false) => {
  if (selected) {
    return "border-orange-500 bg-orange-50 text-stone-900 shadow-[0_18px_45px_rgba(234,88,12,0.12)] ring-1 ring-orange-300/60";
  }

  return subtle
    ? "border-stone-200 bg-stone-50/75 text-stone-800 hover:border-stone-300 hover:bg-stone-100/80"
    : "border-stone-200 bg-white text-stone-900 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50/80";
};

const getPriorityLabel = (value) => PRIORITY_OPTIONS.find((option) => option.value === value)?.label || value;
const getLifestylePreferenceLabel = (value) => LIFESTYLE_PREFERENCE_OPTIONS.find((option) => option.value === value)?.label || value;
const buildLoadedFormData = (data = {}) => ({
  ...EMPTY_FORM_DATA,
  first_name: data.first_name || "",
  current_country: data.current_country || "",
  citizenship: data.citizenship || "",
  age_range: data.age_range || "",
  family_status: data.family_status || "",
  occupation: normalizeOccupation(data.occupation || ""),
  work_location_flexibility: data.work_location_flexibility || "",
  remote_work: Boolean(data.remote_work),
  move_support_paths: inferMoveSupportPathsFromSavedData(data),
  work_abroad_intent: data.work_abroad_intent || (data.remote_work ? "remote_self_employed" : ""),
  budget_range: BUDGET_SELECTION_TO_SCORE_VALUE[data.budget_range] ? data.budget_range : mapBudgetScoreValueToSelection(data.budget_range || ""),
  strict_budget_mode: typeof data.strict_budget_mode === "boolean" ? data.strict_budget_mode : null,
  priorities: data.priorities || [],
  motivations: normalizeMotivations(data.motivations || []),
  lifestyle_preferences: data.lifestyle_preferences || [],
  infrastructure_expectation: data.infrastructure_expectation || "",
  selected_countries: data.selected_countries || [],
  timeline: normalizeTimelineValue(data.timeline || ""),
  intended_length_of_stay: data.intended_length_of_stay || "",
  preferred_climate: data.preferred_climate || "",
  language_flexibility: data.language_flexibility || "",
  healthcare_importance: data.healthcare_importance || "",
  need_reliable_internet: typeof data.need_reliable_internet === "boolean" ? data.need_reliable_internet : null,
  expat_community_importance: data.expat_community_importance || "",
  alcohol_preference: data.alcohol_preference || "",
  cannabis_preference: data.cannabis_preference || "",
});

const REVIEW_STEP_ID = "review";
const REVIEW_STEP_INDEX = ASSESSMENT_STEPS.findIndex((step) => step.id === REVIEW_STEP_ID);

const IntakeForm = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const stepTopRef = useRef(null);
  const hasTrackedAssessmentStartedRef = useRef(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isTransitioningToResults, setIsTransitioningToResults] = useState(false);
  const [resultsTransitionStage, setResultsTransitionStage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [reviewBaseline, setReviewBaseline] = useState(null);
  const [assessmentDataSource, setAssessmentDataSource] = useState("loading");
  const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const createAssessmentSnapshot = (data) => ({
    current_country: data.current_country?.trim() || "",
    citizenship: data.citizenship?.trim() || "",
    motivations: [...normalizeMotivations(data.motivations || [])],
    age_range: data.age_range || "",
    family_status: data.family_status || "",
    occupation: normalizeOccupation(data.occupation || ""),
    work_location_flexibility: data.work_location_flexibility || "",
    remote_work: Boolean(data.remote_work),
    move_support_paths: [...(data.move_support_paths || [])],
    budget_range: data.budget_range || "",
    strict_budget_mode: data.strict_budget_mode ?? null,
    priorities: [...(data.priorities || [])],
    lifestyle_preferences: [...(data.lifestyle_preferences || [])],
    infrastructure_expectation: data.infrastructure_expectation || "",
    preferred_climate: data.preferred_climate || "",
    language_flexibility: data.language_flexibility || "",
    healthcare_importance: data.healthcare_importance || "",
    need_reliable_internet: data.need_reliable_internet ?? null,
    expat_community_importance: data.expat_community_importance || "",
    timeline: normalizeTimelineValue(data.timeline || ""),
    intended_length_of_stay: data.intended_length_of_stay || "",
    selected_countries: [...(data.selected_countries || [])],
  });
  const showDebugPanel = useMemo(() => new URLSearchParams(location.search).get("debug") === "1", [location.search]);
  const requestedStep = useMemo(() => new URLSearchParams(location.search).get("step"), [location.search]);
  const shouldStartOnReviewStep = requestedStep === REVIEW_STEP_ID && REVIEW_STEP_INDEX >= 0;

  useEffect(() => {
    document.title = "Assessment | Relocation Clarity";
    return () => {
      document.title = "Relocation Clarity";
    };
  }, []);

  useEffect(() => {
    const loadIntakeData = async () => {
      try {
        const [intakeResult, countriesResult] = await Promise.allSettled([
          axios.get(`${API}/intake`, { withCredentials: true }),
          axios.get(`${API}/countries`),
        ]);

        if (countriesResult.status === "fulfilled") {
          setAvailableCountries(
            (countriesResult.value.data || []).map((country) => ({
              code: country.code,
              name: country.name,
            }))
          );
        }

        if (intakeResult.status === "fulfilled" && intakeResult.value.data?.current_country) {
          const data = intakeResult.value.data;
          const loadedFormData = buildLoadedFormData(data);

          setFormData(loadedFormData);
          setCurrentStep(shouldStartOnReviewStep ? REVIEW_STEP_INDEX : 0);

          setIsEditing(true);
          setReviewBaseline(createAssessmentSnapshot(loadedFormData));
          setAssessmentDataSource("server-intake");
          setDraftUpdatedAt(null);
          clearAssessmentDraft();
        } else {
          const savedDraft = readAssessmentDraft(user?.email);
          if (savedDraft?.formData) {
            setFormData((prev) => ({ ...prev, ...savedDraft.formData, motivations: normalizeMotivations(savedDraft.formData.motivations || []), timeline: normalizeTimelineValue(savedDraft.formData.timeline || "") }));
            if (shouldStartOnReviewStep) {
              setCurrentStep(0);
            } else if (Number.isInteger(savedDraft.currentStep)) {
              setCurrentStep(Math.max(0, Math.min(savedDraft.currentStep, ASSESSMENT_STEPS.length - 1)));
            }
            setAssessmentDataSource("local-draft");
            setDraftUpdatedAt(savedDraft.updatedAt || null);
          } else {
            setReviewBaseline(null);
            setAssessmentDataSource("empty");
            setDraftUpdatedAt(null);
          }
        }
      } catch (error) {
        console.log("No existing intake data found");

        const savedDraft = readAssessmentDraft(user?.email);
        if (savedDraft?.formData) {
          setFormData((prev) => ({ ...prev, ...savedDraft.formData, motivations: normalizeMotivations(savedDraft.formData.motivations || []), timeline: normalizeTimelineValue(savedDraft.formData.timeline || "") }));
          if (shouldStartOnReviewStep) {
            setCurrentStep(0);
          } else if (Number.isInteger(savedDraft.currentStep)) {
            setCurrentStep(Math.max(0, Math.min(savedDraft.currentStep, ASSESSMENT_STEPS.length - 1)));
          }
          setAssessmentDataSource("local-draft");
          setDraftUpdatedAt(savedDraft.updatedAt || null);
        } else {
          setReviewBaseline(null);
          setAssessmentDataSource("empty");
          setDraftUpdatedAt(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadIntakeData();
  }, [shouldStartOnReviewStep, user?.email]);

  useEffect(() => {
    if (loading || submitting || isTransitioningToResults) return;

    writeAssessmentDraft({
      currentStep,
      formData,
      updatedAt: new Date().toISOString(),
    }, user?.email);
  }, [currentStep, formData, loading, submitting, isTransitioningToResults, user?.email]);

  useEffect(() => {
    if (loading || hasTrackedAssessmentStartedRef.current) return;

    const assessmentStartType = assessmentDataSource === "server-intake"
      ? "resume_existing"
      : assessmentDataSource === "local-draft"
        ? "resume_draft"
        : "new";

    trackGaEvent("assessment_started", {
      page_path: getGaPagePath(),
      assessment_start_type: assessmentStartType,
      assessment_data_source: assessmentDataSource,
      is_editing: isEditing,
    });
    hasTrackedAssessmentStartedRef.current = true;
  }, [assessmentDataSource, isEditing, loading]);

  useEffect(() => {
    const node = stepTopRef.current;
    if (!node) return;

    node.scrollIntoView({ behavior: currentStep === 0 ? "auto" : "smooth", block: "start" });
    window.scrollTo({ top: 0, behavior: currentStep === 0 ? "auto" : "smooth" });
  }, [currentStep]);

  useEffect(() => {
    if (!isTransitioningToResults) return undefined;

    setResultsTransitionStage(0);

    const stageInterval = window.setInterval(() => {
      setResultsTransitionStage((prev) => Math.min(prev + 1, 2));
    }, 1750);

    const navigateTimeout = window.setTimeout(() => {
      navigate("/assessment-results");
    }, 5300);

    return () => {
      window.clearInterval(stageInterval);
      window.clearTimeout(navigateTimeout);
    };
  }, [isTransitioningToResults, navigate]);

  const currentStepMeta = ASSESSMENT_STEPS[currentStep];
  const progress = (currentStep / ASSESSMENT_STEPS.length) * 100;
  const completionPercent = Math.round(progress);
  const isInitialAssessmentScreen = currentStep === 0;
  const resultsTransitionStages = [
    "Reviewing what matters most",
    "Weighing tradeoffs",
    "Preparing your Assessment Results",
  ];
  const resultsTransitionProgress = ((resultsTransitionStage + 1) / resultsTransitionStages.length) * 100;

  const selectedPrioritiesLabel = useMemo(() => `${formData.priorities.length}/5 selected`, [formData.priorities.length]);
  const selectedMotivationsLabel = useMemo(() => `${formData.motivations.length}/3 selected`, [formData.motivations.length]);
  const selectedMoveSupportPathsLabel = useMemo(() => `${formData.move_support_paths.length} selected`, [formData.move_support_paths.length]);
  const selectedLifestylePreferencesLabel = useMemo(() => `${formData.lifestyle_preferences.length}/3 selected`, [formData.lifestyle_preferences.length]);
  const selectedCountriesLabel = useMemo(() => `${formData.selected_countries.length}/3 selected`, [formData.selected_countries.length]);
  const reviewStepChips = [
    { id: "motivations", label: "Why now" },
    { id: "starting_point", label: "Starting point" },
    { id: "age_range", label: "Age" },
    { id: "household_setup", label: "Household" },
    { id: "work_profile", label: "Work & Income" },
    { id: "move_support_paths", label: "Move Support" },
    { id: "budget", label: "Budget" },
    { id: "priorities", label: "Priorities" },
    { id: "lifestyle_preferences", label: "Lifestyle" },
    { id: "infrastructure", label: "Infrastructure" },
    { id: "climate", label: "Climate" },
    { id: "language", label: "Language" },
    { id: "healthcare", label: "Healthcare" },
    { id: "settling_needs", label: "Settling in" },
    { id: "timing", label: "Timeline" },
    { id: "countries_to_keep", label: "Keep in view" },
    { id: "review", label: "Review" },
  ];
  const planAccess = user?.plan_access || {};
  const hasPaidPlan = Boolean(user?.has_paid);
  const assessmentRefreshBanner = useMemo(() => {
    if (!hasPaidPlan) return null;
    if (planAccess.requires_new_purchase_for_refresh) {
      return {
        tone: "border-stone-300 bg-stone-50 text-stone-700",
        title: "Your included refresh has already been used.",
        body: "You can continue updating your assessment as your preferences change. To generate a new Blueprint based on those updates, you’ll need to purchase a new one.",
      };
    }
    if (planAccess.included_refresh_available) {
      return {
        tone: "border-orange-200 bg-orange-50/80 text-stone-700",
        title: "Your paid Blueprint includes one free refresh.",
        body: "If you update your assessment, we’ll generate a refreshed Blueprint at no extra charge.",
      };
    }
    return {
      tone: "border-stone-300 bg-stone-50 text-stone-700",
      title: "Your current paid Blueprint is still available.",
      body: "If you later update your answers again, an additional refreshed Blueprint may require a new purchase.",
    };
  }, [hasPaidPlan, planAccess]);
  const hasAssessmentChanges = useMemo(() => {
    if (!isEditing || !reviewBaseline) return false;
    return JSON.stringify(createAssessmentSnapshot(formData)) !== JSON.stringify(reviewBaseline);
  }, [formData, isEditing, reviewBaseline]);
  const requiresNewPlanForChanges = useMemo(() => {
    if (!hasAssessmentChanges || !hasPaidPlan) return false;
    return planAccess.requires_new_purchase_for_refresh || !planAccess.included_refresh_available;
  }, [hasAssessmentChanges, hasPaidPlan, planAccess.included_refresh_available, planAccess.requires_new_purchase_for_refresh]);
  const reviewStatusMessage = useMemo(() => {
    if (!hasAssessmentChanges) return null;
    return requiresNewPlanForChanges
      ? "You’ve updated your answers. To see these changes reflected in a new Blueprint, you’ll need to purchase a new one."
      : "You’ve updated your answers. Review them once more, then continue to refresh your Assessment Results.";
  }, [hasAssessmentChanges, requiresNewPlanForChanges]);
  const reviewPrimaryButtonLabel = useMemo(() => {
    if (currentStepMeta?.id !== "review") return null;
    if (!hasAssessmentChanges) return "Continue";
    return requiresNewPlanForChanges ? "Continue to New Blueprint" : "Update Results";
  }, [currentStepMeta?.id, hasAssessmentChanges, requiresNewPlanForChanges]);
  const jumpToStep = (stepId) => {
    const stepIndex = ASSESSMENT_STEPS.findIndex((step) => step.id === stepId);
    if (stepIndex >= 0) {
      setCurrentStep(stepIndex);
      requestAnimationFrame(() => {
        stepTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };
  const rankedPriorityEntries = useMemo(
    () => formData.priorities.slice(0, 5).map((priority, index) => ({
      value: priority,
      label: getPriorityLabel(priority),
      rank: index + 1,
    })),
    [formData.priorities]
  );
  const rankedLifestylePreferenceEntries = useMemo(
    () => formData.lifestyle_preferences.slice(0, 3).map((preference, index) => ({
      value: preference,
      label: getLifestylePreferenceLabel(preference),
      rank: index + 1,
    })),
    [formData.lifestyle_preferences]
  );

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePriorityToggle = (value) => {
    setFormData((prev) => {
      if (prev.priorities.includes(value)) {
        return { ...prev, priorities: prev.priorities.filter((item) => item !== value) };
      }

      if (prev.priorities.length >= 5) {
        toast.info("Choose up to 5 so the assessment stays focused on what matters most.");
        return prev;
      }

      return { ...prev, priorities: [...prev.priorities, value] };
    });
  };

  const handlePriorityReorder = (priorityValue, direction) => {
    setFormData((prev) => {
      const currentIndex = prev.priorities.indexOf(priorityValue);
      if (currentIndex === -1) return prev;

      const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prev.priorities.length) return prev;

      const nextPriorities = [...prev.priorities];
      [nextPriorities[currentIndex], nextPriorities[targetIndex]] = [nextPriorities[targetIndex], nextPriorities[currentIndex]];

      return {
        ...prev,
        priorities: nextPriorities,
      };
    });
  };

  const handleLifestylePreferenceToggle = (value) => {
    setFormData((prev) => {
      if (prev.lifestyle_preferences.includes(value)) {
        return { ...prev, lifestyle_preferences: prev.lifestyle_preferences.filter((item) => item !== value) };
      }

      if (prev.lifestyle_preferences.length >= 3) {
        toast.info("Choose up to 3 day-to-day refiners.");
        return prev;
      }

      return { ...prev, lifestyle_preferences: [...prev.lifestyle_preferences, value] };
    });
  };

  const handleLifestylePreferenceReorder = (preferenceValue, direction) => {
    setFormData((prev) => {
      const currentIndex = prev.lifestyle_preferences.indexOf(preferenceValue);
      if (currentIndex === -1) return prev;

      const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prev.lifestyle_preferences.length) return prev;

      const nextPreferences = [...prev.lifestyle_preferences];
      [nextPreferences[currentIndex], nextPreferences[targetIndex]] = [nextPreferences[targetIndex], nextPreferences[currentIndex]];

      return {
        ...prev,
        lifestyle_preferences: nextPreferences,
      };
    });
  };

  const handleLimitedMultiSelect = (field, value, maxSelections, toastMessage) => {
    setFormData((prev) => {
      const currentValues = prev[field] || [];

      if (currentValues.includes(value)) {
        return { ...prev, [field]: currentValues.filter((item) => item !== value) };
      }

      if (currentValues.length >= maxSelections) {
        toast.info(toastMessage);
        return prev;
      }

      return { ...prev, [field]: [...currentValues, value] };
    });
  };

  const handleMoveSupportPathToggle = (value) => {
    setFormData((prev) => {
      const currentValues = prev.move_support_paths || [];
      const earningDependentPaths = ["remote_income", "self_employment", "local_employment"];

      if (value === "not_sure") {
        return {
          ...prev,
          move_support_paths: currentValues.includes("not_sure") ? [] : ["not_sure"],
        };
      }

      if (value === "no_income_required") {
        const baseValues = currentValues.filter((item) => item !== "not_sure" && !earningDependentPaths.includes(item));
        return {
          ...prev,
          move_support_paths: currentValues.includes("no_income_required")
            ? baseValues.filter((item) => item !== "no_income_required")
            : [...baseValues, "no_income_required"],
        };
      }

      const filteredValues = currentValues.filter((item) => item !== "not_sure");
      const normalizedValues = earningDependentPaths.includes(value)
        ? filteredValues.filter((item) => item !== "no_income_required")
        : filteredValues;

      if (normalizedValues.includes(value)) {
        return {
          ...prev,
          move_support_paths: normalizedValues.filter((item) => item !== value),
        };
      }

      return {
        ...prev,
        move_support_paths: [...normalizedValues, value],
      };
    });
  };

  const handleSelectedCountryToggle = (countryCode) => {
    setFormData((prev) => {
      if (prev.selected_countries.includes(countryCode)) {
        return {
          ...prev,
          selected_countries: prev.selected_countries.filter((code) => code !== countryCode),
        };
      }

      if (prev.selected_countries.length >= 3) {
        toast.info("Choose up to 3 countries to keep in view.");
        return prev;
      }

      return {
        ...prev,
        selected_countries: [...prev.selected_countries, countryCode],
      };
    });
  };

  const isStepValid = (stepId = currentStepMeta?.id) => {
    switch (stepId) {
      case "starting_point":
        return formData.current_country.trim() && formData.citizenship.trim();
      case "age_range":
        return Boolean(formData.age_range);
      case "motivations":
        return formData.motivations.length > 0;
      case "household_setup":
        return Boolean(formData.family_status);
      case "work_profile":
        return Boolean(formData.occupation)
          && (!needsWorkLocationFlexibilityFollowUp(formData.occupation) || Boolean(formData.work_location_flexibility));
      case "move_support_paths":
        return formData.move_support_paths.length > 0;
      case "budget":
        return Boolean(formData.budget_range) && formData.strict_budget_mode !== null;
      case "priorities":
        return formData.priorities.length > 0;
      case "lifestyle_preferences":
        return currentStepMeta?.optional ? true : formData.lifestyle_preferences.length > 0;
      case "infrastructure":
        return Boolean(formData.infrastructure_expectation);
      case "climate":
        return Boolean(formData.preferred_climate);
      case "language":
        return Boolean(formData.language_flexibility);
      case "healthcare":
        return Boolean(formData.healthcare_importance);
      case "settling_needs":
        return formData.need_reliable_internet !== null && Boolean(formData.expat_community_importance);
      case "timing":
        return Boolean(formData.timeline) && Boolean(formData.intended_length_of_stay);
      case "countries_to_keep":
      case "review":
        return true;
      default:
        return false;
    }
  };

  const validationHint = useMemo(() => {
    switch (currentStepMeta?.id) {
      case "starting_point":
        return "Add your current country and citizenship to continue.";
      case "age_range":
        return "Select one age range to continue.";
      case "motivations":
        return "Choose at least one reason that matters to you right now.";
      case "household_setup":
        return "Choose the household setup that fits best.";
      case "work_profile":
        return needsWorkLocationFlexibilityFollowUp(formData.occupation) && !formData.work_location_flexibility
          ? "Choose how location-dependent your work is to continue."
          : "Choose the work or income situation that fits best.";
      case "move_support_paths":
        return "Choose at least one way the move could be supported.";
      case "budget":
        return "Choose both your budget range and how strictly to apply it.";
      case "priorities":
        return "Select at least one priority to continue.";
      case "lifestyle_preferences":
        return "Choose at least one secondary lifestyle preference to continue.";
      case "infrastructure":
        return "Choose the level of infrastructure that feels acceptable to you.";
      case "climate":
        return "Choose the environment that feels best.";
      case "language":
        return "Choose your language comfort level.";
      case "healthcare":
        return "Choose how strongly healthcare should shape your leading options.";
      case "settling_needs":
        return "Choose your internet requirement and community preference.";
      case "timing":
        return "Choose both your timing and intended stay length.";
      default:
        return "";
    }
  }, [currentStepMeta?.id]);

  const handleNext = () => {
    if (!isStepValid()) return;
    if (currentStep < ASSESSMENT_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const payload = {
      ...formData,
      first_name: formData.first_name.trim(),
      current_country: formData.current_country.trim(),
      citizenship: formData.citizenship.trim(),
      occupation: formData.occupation,
      work_location_flexibility: needsWorkLocationFlexibilityFollowUp(formData.occupation)
        ? formData.work_location_flexibility
        : null,
      move_support_paths: formData.move_support_paths,
      motivations: normalizeMotivations(formData.motivations),
      work_abroad_intent: deriveWorkAbroadIntentFromMoveSupportPaths(formData.move_support_paths),
      budget_range: mapBudgetSelectionToScoreValue(formData.budget_range),
      budget_display_range: formData.budget_range,
      timeline: normalizeTimelineValue(formData.timeline),
      preferred_climate: formData.preferred_climate === "cooler" ? "temperate" : formData.preferred_climate,
      natural_language_intro: "",
      additional_notes: "",
      remote_work: deriveWorkAbroadIntentFromMoveSupportPaths(formData.move_support_paths) === "remote_self_employed",
      strict_budget_mode: formData.strict_budget_mode === true,
      request_followup: false,
    };

    try {
      const response = await axios.post(`${API}/intake`, payload, { withCredentials: true });
      const refreshState = response.data?.plan_refresh;
      clearAssessmentDraft();

      if (refreshState?.included_refresh_applied) {
        toast.success("Assessment updated. Your included Blueprint refresh has been applied at no extra charge.");
      } else if (refreshState?.requires_new_purchase_for_refresh) {
        toast.success("Assessment updated. Your paid Blueprint remains based on your last paid assessment until you purchase a refreshed Blueprint.");
      } else {
        toast.success(isEditing ? "Assessment updated successfully!" : "Assessment saved successfully!");
      }
      trackGaEvent("assessment_completed", {
        assessment_mode: isEditing ? "update" : "new",
      });
      setIsTransitioningToResults(true);
    } catch (error) {
      console.error("Intake error:", error);
      toast.error("Failed to save assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const renderChoiceCard = ({ keyValue, selected, title, description, onClick, testId, subtle = false }) => (
    <button
      key={keyValue}
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected}
      className={`group w-full rounded-[1.5rem] border px-5 py-4 text-left transition-[transform,background-color,border-color,color,box-shadow] duration-200 ${getChoiceCardClasses(selected, subtle)}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-manrope text-base leading-6">{title}</p>
          {description ? (
            <p className={`mt-2 text-sm leading-6 ${selected ? "text-stone-700" : "text-stone-600"}`}>{description}</p>
          ) : null}
        </div>
        <span
          className={`mt-1 h-4 w-4 rounded-full border ${selected ? "border-orange-500 bg-orange-500" : "border-stone-300 bg-white"}`}
          aria-hidden="true"
        />
      </div>
    </button>
  );

  const renderChipButton = ({ keyValue, selected, label, onClick, testId }) => (
    <button
      key={keyValue}
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected}
      className={`rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color,transform,box-shadow] duration-200 ${selected ? "border-orange-500 bg-orange-50 text-stone-900 shadow-[0_8px_20px_rgba(234,88,12,0.12)]" : "border-stone-200 bg-white text-stone-700 hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50/40"}`}
    >
      {label}
    </button>
  );

  const renderCountryChip = (country) => {
    const selected = formData.selected_countries.includes(country.code);
    return (
      <button
        key={country.code}
        type="button"
        onClick={() => handleSelectedCountryToggle(country.code)}
        data-testid={`selected-country-chip-${country.code}`}
        aria-pressed={selected}
        className={`flex items-center justify-between gap-3 rounded-[1.3rem] border px-4 py-3 text-left transition-[background-color,border-color,color,transform,box-shadow] duration-200 ${selected ? "border-orange-500 bg-orange-50 text-stone-900 shadow-[0_10px_24px_rgba(234,88,12,0.12)]" : "border-stone-200 bg-white text-stone-800 hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50/40"}`}
      >
        <CountryLabel code={country.code} name={country.name} size={16} flagTestId={`selected-country-flag-${country.code}`} />
        <span className={`h-4 w-4 rounded-full border ${selected ? "border-orange-500 bg-orange-500" : "border-stone-300 bg-white"}`} aria-hidden="true" />
      </button>
    );
  };

  const renderStepContent = () => {
    switch (currentStepMeta.id) {
      case "starting_point":
        return (
          <div className="grid gap-5 md:grid-cols-2" data-testid="assessment-starting-point-step">
            <div className="space-y-2">
              <Label htmlFor="current_country">Country you live in now</Label>
              <Input
                id="current_country"
                data-testid="input-current-country"
                className="rc-form-input"
                placeholder="e.g., United States"
                value={formData.current_country}
                onChange={(e) => updateField("current_country", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="citizenship">Citizenship or passport</Label>
              <Input
                id="citizenship"
                data-testid="input-citizenship"
                className="rc-form-input"
                placeholder="e.g., United States"
                value={formData.citizenship}
                onChange={(e) => updateField("citizenship", e.target.value)}
              />
            </div>
          </div>
        );

      case "age_range":
        return (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="assessment-age-range-step">
            {AGE_OPTIONS.map((option) => renderChoiceCard({
              keyValue: option.value,
              selected: formData.age_range === option.value,
              title: option.label,
              onClick: () => updateField("age_range", option.value),
              testId: `age-range-card-${option.value}`,
            }))}
          </div>
        );

      case "motivations":
        return (
          <div className="space-y-4" data-testid="assessment-motivations-step">
            <div className="flex items-center justify-between gap-4 rounded-[1.3rem] border border-stone-200 bg-orange-50/70 px-4 py-3">
              <p className="text-sm text-stone-700">Choose up to 3 reasons that matter most to you right now.</p>
              <p className="text-sm font-medium text-stone-900" data-testid="motivation-count-label">{selectedMotivationsLabel}</p>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="motivation-chip-grid">
              {MOTIVATION_OPTIONS.map((option) => renderChipButton({
                keyValue: option.id,
                selected: formData.motivations.includes(option.id),
                label: option.label,
                onClick: () => handleLimitedMultiSelect("motivations", option.id, 3, "Choose up to 3 reasons that matter most to you right now."),
                testId: `motivation-chip-${option.id}`,
              }))}
            </div>
          </div>
        );

      case "household_setup":
        return (
          <div className="grid gap-3 md:grid-cols-2" data-testid="assessment-household-step">
            {HOUSEHOLD_OPTIONS.map((option) => renderChoiceCard({
              keyValue: option.value,
              selected: formData.family_status === option.value,
              title: option.label,
              description: option.description,
              onClick: () => updateField("family_status", option.value),
              testId: `household-card-${option.value}`,
            }))}
          </div>
        );

      case "work_profile":
        return (
          <div className="space-y-6" data-testid="assessment-work-profile-step">
            <div className="grid gap-3 md:grid-cols-2">
              {WORK_PROFILE_OPTIONS.map((option) => renderChoiceCard({
                keyValue: option.value,
                selected: formData.occupation === option.value,
                title: option.label,
                description: option.description,
                onClick: () => setFormData((prev) => ({
                  ...prev,
                  occupation: option.value,
                  work_location_flexibility: needsWorkLocationFlexibilityFollowUp(option.value)
                    ? prev.work_location_flexibility
                    : "",
                })),
                testId: `work-profile-card-${option.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
              }))}
            </div>
            {needsWorkLocationFlexibilityFollowUp(formData.occupation) ? (
              <div className="space-y-4 rounded-[1.6rem] border border-stone-200 bg-stone-50/80 p-5" data-testid="work-location-flexibility-follow-up">
                <div className="space-y-1">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Work setup</p>
                  <h3 className="text-lg font-semibold text-stone-900" data-testid="work-location-flexibility-question">How location-dependent is your work?</h3>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {WORK_LOCATION_FLEXIBILITY_OPTIONS.map((option) => renderChoiceCard({
                    keyValue: option.value,
                    selected: formData.work_location_flexibility === option.value,
                    title: option.label,
                    description: option.description,
                    onClick: () => updateField("work_location_flexibility", option.value),
                    testId: `work-location-flexibility-card-${option.value}`,
                  }))}
                </div>
              </div>
            ) : null}
          </div>
        );

      case "move_support_paths":
        return (
          <div className="space-y-4" data-testid="assessment-move-support-paths-step">
            <div className="flex items-center justify-between gap-4 rounded-[1.3rem] border border-stone-200 bg-stone-50/80 px-4 py-3">
                  <p className="text-sm text-stone-700">Select all that apply.</p>
              <p className="text-sm font-medium text-stone-900" data-testid="move-support-paths-count-label">{selectedMoveSupportPathsLabel}</p>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="move-support-paths-chip-grid">
              {MOVE_SUPPORT_OPTIONS.map((option) => renderChipButton({
                keyValue: option.value,
                selected: formData.move_support_paths.includes(option.value),
                label: option.label,
                onClick: () => handleMoveSupportPathToggle(option.value),
                testId: `move-support-path-chip-${option.value}`,
              }))}
            </div>
          </div>
        );

      case "budget":
        return (
          <div className="space-y-8" data-testid="assessment-budget-step">
            <div className="space-y-3">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Monthly budget range</p>
              <div className="grid gap-3 md:grid-cols-2">
                {BUDGET_OPTIONS.map((option) => renderChoiceCard({
                  keyValue: option.value,
                  selected: formData.budget_range === option.value,
                  title: option.label,
                  description: option.description,
                  onClick: () => updateField("budget_range", option.value),
                  testId: `budget-card-${option.value}`,
                }))}
              </div>
            </div>

            <div className="space-y-3 rounded-[1.7rem] border border-stone-200 bg-stone-50/80 p-5" data-testid="strict-budget-mode-section">
              <div className="space-y-1">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Budget flexibility</p>
                <p className="text-sm leading-6 text-stone-600">Should we keep your leading options close to your stated budget, or allow some stretch if the broader case is stronger?</p>
              </div>
              <div className="grid gap-3">
                {BUDGET_MODE_OPTIONS.map((option) => renderChoiceCard({
                  keyValue: option.label,
                  selected: formData.strict_budget_mode === option.value,
                  title: option.label,
                  description: option.description,
                  onClick: () => updateField("strict_budget_mode", option.value),
                  testId: `strict-budget-card-${option.value ? "strict" : "flexible"}`,
                  subtle: true,
                }))}
              </div>
            </div>
          </div>
        );

      case "priorities":
        return (
          <div className="space-y-5" data-testid="assessment-priorities-step">
            <div className="flex items-center justify-between gap-4 rounded-[1.3rem] border border-stone-200 bg-stone-50/80 px-4 py-3">
              <p className="text-sm text-stone-700">Choose up to 5 priorities that should carry the most weight.</p>
              <p className="text-sm font-medium text-stone-900" data-testid="priority-count-label">{selectedPrioritiesLabel}</p>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="priority-chip-grid">
              {PRIORITY_OPTIONS.map((option) => renderChipButton({
                keyValue: option.value,
                selected: formData.priorities.includes(option.value),
                label: option.label,
                onClick: () => handlePriorityToggle(option.value),
                testId: `priority-chip-${option.value}`,
              }))}
            </div>

            {rankedPriorityEntries.length >= 2 ? (
              <div className="rounded-[1.6rem] border border-stone-200 bg-white/90 p-5 shadow-[0_18px_45px_rgba(28,25,23,0.06)]" data-testid="priority-ranking-panel">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Your ranked priorities</p>
                  <p className="text-sm leading-6 text-stone-600" data-testid="priority-ranking-helper-copy">
                    Most important at the top. Use the arrows to put them in the order you would want them weighed.
                  </p>
                </div>

                <ol className="mt-4 space-y-3" data-testid="priority-ranking-list">
                  {rankedPriorityEntries.map((priority, index) => {
                    const isFirst = index === 0;
                    const isLast = index === rankedPriorityEntries.length - 1;

                    return (
                      <li
                        key={priority.value}
                        className="flex flex-col gap-3 rounded-[1.35rem] border border-stone-200 bg-stone-50/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                        data-testid={`priority-ranking-item-${priority.value}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white" data-testid={`priority-ranking-badge-${priority.value}`}>
                            {priority.rank}
                          </div>
                          <p className="text-sm font-semibold text-stone-900" data-testid={`priority-ranking-label-${priority.value}`}>{priority.label}</p>
                        </div>

                        <div className="flex items-center gap-2 self-start sm:self-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => handlePriorityReorder(priority.value, "up")}
                            disabled={isFirst}
                            className="h-10 w-10 rounded-full border-stone-300 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-stone-900 disabled:opacity-45"
                            data-testid={`priority-move-up-${priority.value}`}
                            aria-label={`Move ${priority.label} up`}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => handlePriorityReorder(priority.value, "down")}
                            disabled={isLast}
                            className="h-10 w-10 rounded-full border-stone-300 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-stone-900 disabled:opacity-45"
                            data-testid={`priority-move-down-${priority.value}`}
                            aria-label={`Move ${priority.label} down`}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : formData.priorities.length === 1 ? (
              <div className="rounded-[1.35rem] border border-dashed border-stone-300 bg-stone-50/70 px-4 py-4" data-testid="priority-ranking-placeholder">
                <p className="text-sm leading-6 text-stone-600">
                  Add one more priority and we’ll let you rank the selected criteria from most to least important.
                </p>
              </div>
            ) : null}
          </div>
        );

      case "lifestyle_preferences":
        return (
          <div className="space-y-5" data-testid="assessment-lifestyle-preferences-step">
            <div className="flex items-center justify-between gap-4 rounded-[1.3rem] border border-stone-200 bg-orange-50/70 px-4 py-3">
              <p className="text-sm text-stone-700">Choose up to 3. These help fine-tune close calls without outweighing the bigger decision factors.</p>
              <p className="text-sm font-medium text-stone-900" data-testid="lifestyle-preferences-count-label">{selectedLifestylePreferencesLabel}</p>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="lifestyle-preferences-chip-grid">
              {LIFESTYLE_PREFERENCE_OPTIONS.map((option) => renderChipButton({
                keyValue: option.value,
                selected: formData.lifestyle_preferences.includes(option.value),
                label: option.label,
                onClick: () => handleLifestylePreferenceToggle(option.value),
                testId: `lifestyle-preference-chip-${option.value}`,
              }))}
            </div>

            {rankedLifestylePreferenceEntries.length >= 2 ? (
              <div className="rounded-[1.6rem] border border-stone-200 bg-white/90 p-5 shadow-[0_18px_45px_rgba(28,25,23,0.06)]" data-testid="lifestyle-ranking-panel">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Your ranked lifestyle refiners</p>
                  <p className="text-sm leading-6 text-stone-600" data-testid="lifestyle-ranking-helper-copy">
                    Most important at the top. Use the arrows to put them in the order you would want them weighed.
                  </p>
                </div>

                <ol className="mt-4 space-y-3" data-testid="lifestyle-ranking-list">
                  {rankedLifestylePreferenceEntries.map((preference, index) => {
                    const isFirst = index === 0;
                    const isLast = index === rankedLifestylePreferenceEntries.length - 1;

                    return (
                      <li
                        key={preference.value}
                        className="flex flex-col gap-3 rounded-[1.35rem] border border-stone-200 bg-stone-50/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                        data-testid={`lifestyle-ranking-item-${preference.value}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white" data-testid={`lifestyle-ranking-badge-${preference.value}`}>
                            {preference.rank}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-stone-900" data-testid={`lifestyle-ranking-label-${preference.value}`}>{preference.label}</p>
                            <p className="text-xs text-stone-500">
                              {preference.rank === 1 ? "Strongest day-to-day tiebreaker" : preference.rank === 2 ? "Supporting lifestyle tiebreaker" : "Light lifestyle signal"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 self-start sm:self-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => handleLifestylePreferenceReorder(preference.value, "up")}
                            disabled={isFirst}
                            className="h-10 w-10 rounded-full border-stone-300 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-stone-900 disabled:opacity-45"
                            data-testid={`lifestyle-move-up-${preference.value}`}
                            aria-label={`Move ${preference.label} up`}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => handleLifestylePreferenceReorder(preference.value, "down")}
                            disabled={isLast}
                            className="h-10 w-10 rounded-full border-stone-300 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-stone-900 disabled:opacity-45"
                            data-testid={`lifestyle-move-down-${preference.value}`}
                            aria-label={`Move ${preference.label} down`}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : formData.lifestyle_preferences.length === 1 ? (
              <div className="rounded-[1.35rem] border border-dashed border-stone-300 bg-stone-50/70 px-4 py-4" data-testid="lifestyle-ranking-placeholder">
                <p className="text-sm leading-6 text-stone-600">
                  Add one more day-to-day refiner and we’ll let you rank the selected items from most to least important.
                </p>
              </div>
            ) : null}

            <p className="text-sm leading-6 text-stone-500" data-testid="lifestyle-preferences-helper-copy">
              These help break close ties when multiple countries are otherwise quite close. You can skip this step.
            </p>
          </div>
        );

      case "infrastructure":
        return (
          <div className="grid gap-3 md:grid-cols-2" data-testid="assessment-infrastructure-step">
            {INFRASTRUCTURE_OPTIONS.map((option) => renderChoiceCard({
              keyValue: option.value,
              selected: formData.infrastructure_expectation === option.value,
              title: option.label,
              description: option.description,
              onClick: () => updateField("infrastructure_expectation", option.value),
              testId: `infrastructure-card-${option.value}`,
            }))}
          </div>
        );

      case "climate":
        return (
          <div className="grid gap-3 md:grid-cols-2" data-testid="assessment-climate-step">
            {CLIMATE_OPTIONS.map((option) => renderChoiceCard({
              keyValue: option.value,
              selected: formData.preferred_climate === option.value,
              title: option.label,
              onClick: () => updateField("preferred_climate", option.value),
              testId: `climate-card-${option.value}`,
            }))}
          </div>
        );

      case "language":
        return (
          <div className="grid gap-3 md:grid-cols-2" data-testid="assessment-language-step">
            {LANGUAGE_OPTIONS.map((option) => renderChoiceCard({
              keyValue: option.value,
              selected: formData.language_flexibility === option.value,
              title: option.label,
              onClick: () => updateField("language_flexibility", option.value),
              testId: `language-card-${option.value}`,
            }))}
          </div>
        );

      case "healthcare":
        return (
          <div className="grid gap-3 md:grid-cols-2" data-testid="assessment-healthcare-step">
            {HEALTHCARE_OPTIONS.map((option) => renderChoiceCard({
              keyValue: option.value,
              selected: formData.healthcare_importance === option.value,
              title: option.label,
              description: option.description,
              onClick: () => updateField("healthcare_importance", option.value),
              testId: `healthcare-card-${option.value}`,
            }))}
          </div>
        );

      case "settling_needs":
        return (
          <div className="space-y-8" data-testid="assessment-settling-needs-step">
            <div className="space-y-3">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Internet reliability</p>
              <div className="grid gap-3 md:grid-cols-2">
                {INTERNET_OPTIONS.map((option) => renderChoiceCard({
                  keyValue: option.label,
                  selected: formData.need_reliable_internet === option.value,
                  title: option.label,
                  description: option.description,
                  onClick: () => updateField("need_reliable_internet", option.value),
                  testId: `internet-card-${option.value ? "yes" : "no"}`,
                }))}
              </div>
            </div>

            <div className="space-y-3 rounded-[1.7rem] border border-stone-200 bg-stone-50/80 p-5">
              <div className="space-y-1">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Expat community</p>
                <p className="text-sm leading-6 text-stone-600">How helpful would an expat or international community be early on?</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {COMMUNITY_OPTIONS.map((option) => renderChoiceCard({
                  keyValue: option.value,
                  selected: formData.expat_community_importance === option.value,
                  title: option.label,
                  onClick: () => updateField("expat_community_importance", option.value),
                  testId: `community-card-${option.value}`,
                  subtle: true,
                }))}
              </div>
            </div>
          </div>
        );

      case "timing":
        return (
          <div className="space-y-8" data-testid="assessment-timing-step">
            <div className="space-y-3">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Relocation timing</p>
              <div className="grid gap-3 md:grid-cols-2">
                {TIMELINE_OPTIONS.map((option) => renderChoiceCard({
                  keyValue: option.value,
                  selected: formData.timeline === option.value,
                  title: option.label,
                  description: option.description,
                  onClick: () => updateField("timeline", option.value),
                  testId: `timeline-card-${option.value}`,
                }))}
              </div>
            </div>

            <div className="space-y-3 rounded-[1.7rem] border border-stone-200 bg-stone-50/80 p-5">
              <div className="space-y-1">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Intended stay length</p>
                <p className="text-sm leading-6 text-stone-600">This helps distinguish an exploratory move from a longer-term relocation decision.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {STAY_LENGTH_OPTIONS.map((option) => renderChoiceCard({
                  keyValue: option.value,
                  selected: formData.intended_length_of_stay === option.value,
                  title: option.label,
                  onClick: () => updateField("intended_length_of_stay", option.value),
                  testId: `stay-length-card-${option.value}`,
                  subtle: true,
                }))}
              </div>
            </div>
          </div>
        );

      case "countries_to_keep":
        return (
          <div className="space-y-4" data-testid="assessment-selected-countries-step">
            <div className="flex items-center justify-between gap-4 rounded-[1.3rem] border border-stone-200 bg-stone-50/80 px-4 py-3">
              <p className="text-sm text-stone-700">Choose up to 3 countries you want us to keep in view in your Assessment Results.</p>
              <p className="text-sm font-medium text-stone-900" data-testid="selected-countries-count">{selectedCountriesLabel}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2" data-testid="selected-country-chip-grid">
              {availableCountries.map((country) => renderCountryChip(country))}
            </div>
          </div>
        );

      case "review":
        return (
          <div className="space-y-5" data-testid="assessment-review-step">
            {reviewStatusMessage ? (
              <div className="rounded-[1.4rem] border border-stone-200 bg-stone-50/80 px-4 py-3 text-sm leading-7 text-stone-700" data-testid="assessment-review-status-message">
                {reviewStatusMessage}
              </div>
            ) : null}
            <div className="rounded-[1.6rem] border border-stone-200 bg-stone-50/80 p-5 sm:p-6" data-testid="review-step-chip-shell">
              <div className="flex flex-wrap gap-3" data-testid="review-step-chip-grid">
                {reviewStepChips.map((stepChip, index) => {
                  const isActive = stepChip.id === currentStepMeta.id;
                  return (
                    <Button
                      key={stepChip.id}
                      type="button"
                      variant="ghost"
                      onClick={() => jumpToStep(stepChip.id)}
                      className={`h-auto rounded-full border px-4 py-2 text-left transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 ${isActive ? "border-orange-300 bg-orange-50 text-stone-900" : "border-stone-300 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-stone-900"}`}
                      data-testid={`review-step-chip-${stepChip.id}`}
                    >
                      <span className="text-sm font-medium">{index + 1} {stepChip.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground text-sm">Loading your assessment data...</p>
        </div>
      </div>
    );
  }

  if (isTransitioningToResults) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="assessment-results-transition-screen">
        <div className="w-full max-w-xl rounded-[2rem] border border-stone-200/80 bg-white/92 p-8 text-center shadow-[0_24px_60px_rgba(28,25,23,0.08)] sm:p-10">
          <p className="rc-section-kicker mb-3" data-testid="assessment-results-transition-kicker">Assessment</p>
          <h1 className="font-manrope text-3xl text-stone-900 sm:text-4xl" data-testid="assessment-results-transition-title">
            Preparing your Assessment Results
          </h1>
          <p className="mt-4 text-base leading-7 text-stone-600" data-testid="assessment-results-transition-copy">
            We’re turning your answers into a clearer set of leading options.
          </p>

          <div className="mt-8 rounded-[1.5rem] border border-stone-200 bg-stone-50/85 p-5 text-left" data-testid="assessment-results-transition-progress-shell">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-900" data-testid="assessment-results-transition-active-stage">
              <span className="inline-flex gap-1" aria-hidden="true">
                <span className="h-2 w-2 animate-pulse rounded-full bg-orange-500 [animation-delay:0ms]" />
                <span className="h-2 w-2 animate-pulse rounded-full bg-orange-400 [animation-delay:150ms]" />
                <span className="h-2 w-2 animate-pulse rounded-full bg-orange-300 [animation-delay:300ms]" />
              </span>
              <span>{resultsTransitionStages[resultsTransitionStage]}</span>
            </div>
            <Progress
              value={resultsTransitionProgress}
              className="mt-4 h-2 bg-orange-100 [&>div]:bg-orange-500"
              data-testid="assessment-results-transition-progress-bar"
            />
            <div className="mt-5 space-y-2" data-testid="assessment-results-transition-stage-list">
              {resultsTransitionStages.map((stage, index) => {
                const isActive = index === resultsTransitionStage;
                const isComplete = index < resultsTransitionStage;

                return (
                  <div
                    key={stage}
                    className={`flex items-center gap-3 text-sm ${isActive ? "text-stone-900" : "text-stone-500"}`}
                    data-testid={`assessment-results-transition-stage-${index + 1}`}
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${isComplete ? "bg-orange-500" : isActive ? "bg-orange-400" : "bg-stone-300"}`}
                      aria-hidden="true"
                    />
                    <span className={isActive ? "font-medium" : "font-normal"}>{stage}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-page-shell min-h-screen bg-background flex flex-col">
      <header className="rc-header">
        <div className="rc-container">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="assessment-header-home-link">
              <div className="flex items-center gap-2">
                <Globe className="w-7 h-7 text-amber-400" />
                <span className="font-manrope font-semibold text-xl tracking-tight text-stone-50">Relocation Clarity</span>
              </div>
              <span className="hidden lg:inline text-sm font-normal text-stone-300/90">
                Independent Relocation Guidance
              </span>
            </Link>
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="rounded-full border border-stone-700 bg-stone-900/80 px-4 py-2 text-stone-300 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:text-stone-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="rc-container py-8 md:py-12">
        <div ref={stepTopRef} className={`mx-auto flex w-full max-w-4xl flex-col ${isInitialAssessmentScreen ? "gap-6" : "gap-4"}`}>
          {showDebugPanel ? (
            <div className="rounded-[1.4rem] border border-amber-200 bg-amber-50/80 px-5 py-4 text-sm text-stone-700" data-testid="assessment-debug-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700" data-testid="assessment-debug-heading">Preview debug</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="assessment-debug-grid">
                <p data-testid="assessment-debug-email"><span className="font-medium text-stone-900">User email:</span> {user?.email || "Unavailable"}</p>
                <p data-testid="assessment-debug-plan-id"><span className="font-medium text-stone-900">Current plan ID:</span> {user?.plan_access?.current_plan_id || "None"}</p>
                <p data-testid="assessment-debug-plan-created-at"><span className="font-medium text-stone-900">Plan generated:</span> {user?.plan_access?.current_plan_created_at || "None"}</p>
                <p data-testid="assessment-debug-source"><span className="font-medium text-stone-900">Assessment source:</span> {assessmentDataSource}</p>
                <p data-testid="assessment-debug-draft-updated-at"><span className="font-medium text-stone-900">Draft updated:</span> {draftUpdatedAt || "None"}</p>
                <p data-testid="assessment-debug-plan-intake-id"><span className="font-medium text-stone-900">Plan intake ID:</span> {user?.plan_access?.current_plan_based_on_intake_id || "None"}</p>
              </div>
            </div>
          ) : null}
          {isInitialAssessmentScreen ? (
            <div className="rc-panel px-7 py-8 sm:px-10" data-testid="assessment-intro-section">
              <p className="rc-section-kicker mb-3">Assessment</p>
              <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="assessment-intro-title">
                Get clear on what you want in a country before you decide where to live abroad.
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-8 text-stone-600" data-testid="assessment-intro-copy">
                This assessment helps you sort out the priorities, tradeoffs, and real-life constraints that should shape your shortlist.
              </p>
            </div>
          ) : null}

          {assessmentRefreshBanner ? (
            <div
              className={`rounded-[1.45rem] border px-5 py-4 ${assessmentRefreshBanner.tone}`}
              data-testid="assessment-plan-refresh-banner"
            >
              <p className="text-sm font-semibold text-stone-900" data-testid="assessment-plan-refresh-banner-title">
                {assessmentRefreshBanner.title}
              </p>
              <p className="mt-2 text-sm leading-7" data-testid="assessment-plan-refresh-banner-copy">
                {assessmentRefreshBanner.body}
              </p>
            </div>
          ) : null}

          <Card className="rc-panel border-stone-200/80 opacity-0 animate-fade-in" data-testid="assessment-form-card">
            <CardHeader className="border-b border-stone-200/70 p-8 sm:p-10">
              <div className="mb-5 grid grid-cols-[auto,1fr,auto] items-center gap-x-4 gap-y-3">
                <div className="justify-self-start">
                  <span className="text-sm font-medium text-stone-700" data-testid="assessment-progress-count">
                    Step {currentStep + 1} of {ASSESSMENT_STEPS.length}
                  </span>
                </div>
                <div className="justify-self-center text-center">
                  <span className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700/90" data-testid="assessment-progress-percent">
                    {completionPercent}% Complete
                  </span>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 justify-self-end">
                  <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-orange-700" data-testid="assessment-step-kicker">
                    {currentStepMeta.optional ? "Optional" : currentStepMeta.section}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => jumpToStep("review")}
                    disabled={currentStepMeta.id === "review"}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${currentStepMeta.id === "review" ? "border-stone-300 bg-stone-100 text-stone-500 cursor-default" : "border-stone-300 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-stone-900"}`}
                    data-testid="assessment-review-edit-button"
                  >
                    Review answers
                  </Button>
                </div>
              </div>
              <Progress value={progress} className="h-2 bg-orange-100 [&>div]:bg-orange-500" data-testid="assessment-progress-bar" />
              <CardTitle className="mt-6 font-manrope text-3xl text-stone-900" data-testid="assessment-step-title">
                {currentStepMeta.question}
              </CardTitle>
              {currentStepMeta.helper ? (
                <CardDescription className="max-w-3xl text-base leading-7 text-stone-600" data-testid="assessment-step-description">
                  {currentStepMeta.helper}
                </CardDescription>
              ) : null}
            </CardHeader>

            <CardContent className="p-8 pt-8 sm:p-10">
              {renderStepContent()}

              {!isStepValid() && !currentStepMeta.optional ? (
                <p className="mt-6 text-sm leading-6 text-stone-500" data-testid="assessment-validation-hint">{validationHint}</p>
              ) : null}

              <div className="mt-10 flex flex-col gap-3 border-t border-stone-200/70 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  {currentStep > 0 ? (
                    <Button
                      data-testid="intake-back-btn"
                      variant="outline"
                      onClick={handleBack}
                      className="rounded-full border-stone-300 px-5 py-2 text-stone-700 hover:border-orange-300 hover:bg-orange-50/60 hover:text-stone-900 focus-visible:ring-orange-300"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>
                  ) : null}
                </div>

                {currentStep < ASSESSMENT_STEPS.length - 1 ? (
                  <Button
                    data-testid="intake-next-btn"
                    onClick={handleNext}
                    disabled={!currentStepMeta.optional && !isStepValid()}
                    className="rounded-full bg-stone-900 px-6 py-2 text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange-600 disabled:hover:translate-y-0"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    data-testid="intake-submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="rounded-full bg-orange-500 px-6 py-2 text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange-600"
                  >
                    {submitting ? "Saving your inputs..." : currentStepMeta.id === "review" ? reviewPrimaryButtonLabel : isEditing ? "Update & View Results" : "See My Assessment Results"}
                    {currentStepMeta.id === "review" ? <ArrowRight className="w-4 h-4 ml-2" /> : <CheckCircle className="w-4 h-4 ml-2" />}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IntakeForm;